import React, { useMemo } from 'react';
import { cx } from '../utils/cx';
import { getImage } from '../utils/images';
import nl2br from '../utils/nl2br.js';
import { translate } from '../utils/wp.js';
import { HotelLink } from './HotelLink';
import { NewDialog } from './NewDialog';
import { HotelMap } from './single-product/hotels/HotelMap';

function hasSection(sections, section) {
    return sections.indexOf(section) > -1;
}

function HotelInfoDialog({
    visible,
    section,
    sections,
    hotel,
    tickets,
    onToggleSection,
    onClose,
}) {
    if (!sections) {
        sections = ['about', 'images'];
    }

    const images = useMemo(() => {
        return hotel.image_gallery.map(({image}) => {
            const url = getImage(image);

            if (!url) {
                return null;
            }

            return url;
        }).filter(Boolean);
    }, [hotel]);

    const aboutButtonClasses = cx(
        section === 'about' && 'active !border-primary',
    );

    const galleryButtonClasses = cx(
        section === 'images' && 'active !border-primary',
    );

    return (
        <NewDialog visible={visible} maxHeight={true} onClose={onClose}>
            <div className={'relative'}>
                <div className="tabs flex flex-start items-start pl-3 pr-8 pt-4 border-b border-black/10 sm:pr-12 sm:pl-4">
                    {hasSection(sections, 'about') &&
                        <button
                            className={cx(
                                'pb-3 flex-1 border-b-2 border-transparent flex justify-center items-center gap-x-2.5 sm:pb-4',
                                aboutButtonClasses,
                            )}
                            onClick={() => onToggleSection('about')}
                        >
                            <i className="icon-hotel hidden sm:block text-primary text-[22px]"></i>
                            <span className="text-sm font-semibold sm:text-base">{translate(
                                'information')}</span>
                        </button>
                    }

                    {hasSection(sections, 'images') && images.length > 0 &&
                        <button
                            className={cx(
                                'pb-3 flex-1 border-b-2 border-transparent flex justify-center items-center gap-x-2.5 sm:pb-4',
                                galleryButtonClasses,
                            )}
                            onClick={() => onToggleSection('images')}
                        >
                            <i className="icon-image hidden sm:block text-primary text-[24px]"></i>
                            <span className="text-sm font-semibold sm:text-base">{translate(
                                'gallery')}</span>
                        </button>
                    }
                </div>
                <button
                    className="icon-plus rotate-45 text-[20px] absolute right-2 top-4 sm:top-5 sm:right-4"
                    onClick={onClose}
                />
            </div>
            <div className="dialog__content h-full overflow-y-auto p-4 flex flex-col gap-y-1.5">
                {hasSection(section, 'about') && section === 'about' &&
                    <>
                        <div className="text-lg font-semibold">{translate(
                            'about')} {hotel.name}</div>
                        <div className="text-sm flex-1 flex flex-col gap-y-3">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: nl2br(hotel.content),
                                }}
                            ></div>
                            <div>
                                {translate('read_more_about_hotel')}{' '}
                                <HotelLink href={hotel.website} />
                            </div>
                            <div className="map rounded-lg flex-1">
                                {visible &&
                                    <HotelMap
                                        hotel={hotel}
                                        tickets={tickets}
                                        interactive={true}
                                    />
                                }
                            </div>
                        </div>
                    </>
                }

                {hasSection(sections, 'images') && section === 'images' &&
                    <div className="gallery">
                        <div className="flex items-center gap-x-2 mb-1 text-sm">
                            <i className="icon-info text-[16px] text-alttag"></i>
                            <span>{translate('hotel_rooms_may_differ')}</span>
                        </div>
                        {images.map(image => <img
                            className="w-full mt-3 rounded-lg object-cover object-center"
                            src={image}
                            key={image}
                        />)}
                    </div>
                }
            </div>
        </NewDialog>
    );
}

export { HotelInfoDialog };
